import PaymentExecutionPanel from './payment-execution-panel.jsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../../../shared/ui/card/card.jsx';
import EntityLink from '../../../shared/ui/entity-link/entity-link.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import { getTransactionStatusBadgeClass, isFinalTransactionStatus } from './transaction-status-badge.js';

const MANUAL_ACTION_TYPES = new Set(['deposit', 'withdrawal', 'ib_transfer']);

function getTransactionTypeBadgeClass() {
  return 'bg-secondary-subtle text-secondary';
}

function getProfileStatusBadgeClass(statusValue) {
  const status = String(statusValue || '').trim().toLowerCase();
  const map = {
    verified: 'bg-success-subtle text-success',
    pending: 'bg-warning-subtle text-warning',
    verification: 'bg-warning-subtle text-warning',
    new: 'bg-info-subtle text-info',
    completed: 'bg-primary-subtle text-primary',
    limited: 'bg-secondary-subtle text-secondary',
    disabled: 'bg-danger-subtle text-danger',
  };

  return map[status] || 'bg-secondary-subtle text-secondary';
}

function hasPlatformOrderId(value) {
  const normalizedValue = String(value || '').trim();
  return normalizedValue !== '' && normalizedValue !== '-';
}

function CardTitleWithStatus({ title, status, getBadgeClass = getTransactionStatusBadgeClass }) {
  return (
    <div className="d-flex align-items-center justify-content-between gap-2">
      <span>{title}</span>
      <span className={`badge rounded-pill fw-semibold text-uppercase ${getBadgeClass(status)}`}>
        {status || '-'}
      </span>
    </div>
  );
}

function AccountBalanceValue({ value, coverage }) {
  const { t } = useTranslation();

  if (coverage === 'insufficient') {
    const label = t('transactions.accountBalanceInsufficient', {
      defaultValue: 'Account balance is lower than withdrawal amount',
    });

    return (
      <span className="d-inline-flex align-items-center gap-2">
        <span>{value || '-'}</span>
        <i className="fa fa-triangle-exclamation text-danger" aria-label={label} title={label}></i>
      </span>
    );
  }

  if (coverage === 'ok') {
    const label = t('transactions.accountBalanceSufficient', {
      defaultValue: 'Account balance covers withdrawal amount',
    });

    return (
      <span className="d-inline-flex align-items-center gap-2">
        <span>{value || '-'}</span>
        <i className="fa fa-square-check text-success" aria-label={label} title={label}></i>
      </span>
    );
  }

  return value || '-';
}

function InfoList({ items, translateLabel = null }) {
  return (
    <dl className="app-kv">
      {items.map((item, index) => (
        <div
          key={`${item.label}-${index}`}
          className="app-kv-item"
          style={{ gridTemplateColumns: 'max-content 1fr', columnGap: '16px' }}
        >
          <dt style={{ whiteSpace: 'nowrap' }}>{translateLabel ? translateLabel(item) : item.label}</dt>
          <dd>
            {item.value || '-'}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function TransactionLink({ id }) {
  if (!id) {
    return '-';
  }

  return (
    <EntityLink to={`/transactions/${encodeURIComponent(id)}`} className="text-decoration-none">
      {id}
    </EntityLink>
  );
}

function AccountLinkValue({ account }) {
  if (!account?.id) {
    return account?.login || '-';
  }

  return (
    <EntityLink to={`/accounts/${encodeURIComponent(account.id)}`} className="text-decoration-none">
      {account.login || account.id}
    </EntityLink>
  );
}

function TransactionDetailsPageView({
  transaction,
  isLoading,
  isSaving,
  error,
  success,
  isActionDisabled,
  navigateBack,
  approveTransaction,
  declineTransaction,
  canManageOwnTransfer,
  manualOwnTransferForm,
  updateManualOwnTransferField,
  processOwnTransferNow,
  completeOwnTransferManually,
  allowedPaymentActions = [],
  refreshPaymentExecution,
  recordPaymentEvidence,
  manualEvidenceForm,
  updateManualEvidenceField,
}) {
  const { t } = useTranslation();
  const profilePath = transaction?.profileId ? `/profiles/${encodeURIComponent(transaction.profileId)}` : '';
  const accountPath = transaction?.accountId ? `/accounts/${encodeURIComponent(transaction.accountId)}` : '';
  const transactionType = String(transaction?.type || '').trim().toLowerCase();
  const transactionTypeLabel = transactionType === 'own_transfer'
    ? t('transactions.internalTransfer', { defaultValue: 'Internal Transfer' })
    : transaction?.type;
  const canShowManualActions = !transaction?.paymentExecution && MANUAL_ACTION_TYPES.has(transactionType)
    && !isFinalTransactionStatus(transaction?.status);
  const hasTargetAccount = Boolean(transaction?.targetAccount?.id);
  const isSourcePlatformOrderRecorded = hasPlatformOrderId(transaction?.sourceAccountOrderId || transaction?.accountOrderId);
  const isTargetPlatformOrderRecorded = hasPlatformOrderId(transaction?.targetAccountOrderId);
  const isPlatformOrderRecorded = hasTargetAccount
    ? isSourcePlatformOrderRecorded && isTargetPlatformOrderRecorded
    : hasPlatformOrderId(transaction?.accountOrderId);
  const platformOrderStatus = isPlatformOrderRecorded
    ? t('transactions.platformOrderRecorded', { defaultValue: 'RECORDED' })
    : t('transactions.platformOrderPending', { defaultValue: 'PENDING' });
  const platformOrderStatusClass = isPlatformOrderRecorded
    ? 'bg-success-subtle text-success'
    : 'bg-warning-subtle text-warning';
  const headerActions = transaction ? (
    <div className="d-flex justify-content-end gap-2 flex-wrap">
      <button type="button" className="btn btn-default" onClick={navigateBack} disabled={isSaving}>
        {t('common.cancel')}
      </button>
      {canShowManualActions ? (
        <>
          <button type="button" className="btn btn-success" onClick={approveTransaction} disabled={isSaving || isActionDisabled}>
            {t('transactions.approve')}
          </button>
          <button type="button" className="btn btn-danger" onClick={declineTransaction} disabled={isSaving || isActionDisabled}>
            {t('transactions.decline')}
          </button>
        </>
      ) : null}
      {transaction.paymentExecution ? <>
        {allowedPaymentActions.includes('approve') ? <button type="button" className="btn btn-success" onClick={approveTransaction} disabled={isSaving}>{t('transactions.approve')}</button> : null}
        {allowedPaymentActions.includes('decline') ? <button type="button" className="btn btn-danger" onClick={declineTransaction} disabled={isSaving}>{t('transactions.payment.requestCancellation',{defaultValue:'Request cancellation'})}</button> : null}
      </> : null}
      {canManageOwnTransfer ? (
        <button type="button" className="btn btn-primary" onClick={processOwnTransferNow} disabled={isSaving}>
          {t('transactions.processNow', { defaultValue: 'Process Now' })}
        </button>
      ) : null}
    </div>
  ) : null;

  return (
    <div>
      <FloatingAlerts error={error} success={success} />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <div>
          <h2>{t('transactions.detailsTitle')}</h2>
          <p>{transaction?.id ? `${t('transactions.transactionHash')}${transaction.id}` : t('transactions.title')}</p>
        </div>
        {headerActions}
      </div>

      {isLoading ? <div className="text-muted">{t('transactions.loadingTransaction')}</div> : null}

      {!isLoading && transaction ? (
        <>
          {transaction.paymentExecution ? <div className="mb-3"><PaymentExecutionPanel execution={transaction.paymentExecution} actions={allowedPaymentActions} isSaving={isSaving} refresh={refreshPaymentExecution} recordEvidence={recordPaymentEvidence} form={manualEvidenceForm} updateField={updateManualEvidenceField} /></div> : null}
          <div className="row g-3">
            <div className="col-12 col-md-6 col-xl-3" data-testid="transaction-details-card-column">
              <div data-testid="transaction-profile-card">
                <Card
                  title={(
                    <CardTitleWithStatus
                      title={t('transactions.profileDetails')}
                      status={transaction.profileStatus}
                      getBadgeClass={getProfileStatusBadgeClass}
                    />
                  )}
                >
                  <InfoList
                    items={[
                      {
                        label: t('transactions.fullName'),
                        value: profilePath ? (
                          <EntityLink to={profilePath} className="text-decoration-none">
                            {transaction.profileName}
                          </EntityLink>
                        ) : transaction.profileName,
                      },
                      { label: t('transactions.country'), value: transaction.profileCountry },
                    ]}
                  />
                </Card>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-3" data-testid="transaction-details-card-column">
              <Card
                title={(
                  <CardTitleWithStatus
                    title={(
                      <span
                        className={`badge rounded-pill fw-semibold text-uppercase ${getTransactionTypeBadgeClass()}`}
                        data-testid="transaction-type-badge"
                      >
                        {transactionTypeLabel || '-'}
                      </span>
                    )}
                    status={transaction.status}
                  />
                )}
              >
                <InfoList
                  items={[
                    { label: t('common.type'), value: transactionTypeLabel },
                    { label: t('transactions.method'), value: transaction.methodId },
                    { label: t('transactions.amount'), value: transaction.amountFormatted },
                    {
                      label: hasTargetAccount
                        ? t('transactions.sourceAccountLogin', { defaultValue: 'Source Account Login' })
                        : t('transactions.accountLogin'),
                      value: accountPath ? (
                        <EntityLink to={accountPath} className="text-decoration-none">
                          {transaction.accountLogin}
                        </EntityLink>
                      ) : transaction.accountLogin,
                    },
                    {
                      label: hasTargetAccount
                        ? t('transactions.sourceAccountCurrency', { defaultValue: 'Source Account Currency' })
                        : t('transactions.accountCurrency'),
                      value: transaction.accountCurrency,
                    },
                    hasTargetAccount ? {
                      label: t('transactions.targetAccountLogin', { defaultValue: 'Target Account Login' }),
                      value: <AccountLinkValue account={transaction.targetAccount} />,
                    } : null,
                    hasTargetAccount ? {
                      label: t('transactions.targetAccountCurrency', { defaultValue: 'Target Account Currency' }),
                      value: transaction.targetAccount.currency,
                    } : null,
                    {
                      label: t('transactions.accountBalance'),
                      value: (
                        <AccountBalanceValue
                          value={transaction.accountBalance}
                          coverage={transaction.accountBalanceCoverage}
                        />
                      ),
                    },
                  ].filter(Boolean)}
                />
              </Card>
            </div>

            <div className="col-12 col-md-6 col-xl-3" data-testid="transaction-details-card-column">
              <div data-testid="transaction-platform-card">
                <Card
                  title={(
                    <CardTitleWithStatus
                      title={t('transactions.platformDetails')}
                      status={platformOrderStatus}
                      getBadgeClass={() => platformOrderStatusClass}
                    />
                  )}
                >
                  <InfoList
                    items={[
                      { label: t('transactions.accountName'), value: transaction.accountName },
                      { label: t('transactions.serverName'), value: transaction.tradingServerName },
                      { label: t('transactions.platform'), value: transaction.platform },
                      {
                        label: hasTargetAccount
                          ? t('transactions.sourcePlatformOrderId', { defaultValue: 'Source Platform Order ID' })
                          : t('transactions.platformOrderId'),
                        value: transaction.sourceAccountOrderId || transaction.accountOrderId,
                      },
                      hasTargetAccount ? {
                        label: t('transactions.targetPlatformOrderId', { defaultValue: 'Target Platform Order ID' }),
                        value: transaction.targetAccountOrderId,
                      } : null,
                    ].filter(Boolean)}
                  />
                </Card>
              </div>
            </div>

            {transaction.ownTransferCalculationRows?.length ? (
              <div className="col-12 col-md-6 col-xl-3" data-testid="transaction-details-card-column">
                <Card title={t('transactions.ownTransferDetails', { defaultValue: 'Internal Transfer Details' })}>
                  <InfoList
                    items={transaction.ownTransferCalculationRows}
                    translateLabel={(item) => (
                      item.labelKey ? t(item.labelKey, { defaultValue: item.label }) : item.label
                    )}
                  />
                </Card>
              </div>
            ) : null}

            {canManageOwnTransfer ? (
              <div className="col-12 col-md-6 col-xl-3" data-testid="transaction-details-card-column">
                <Card title={t('transactions.ownTransferOperations', { defaultValue: 'Internal Transfer Operations' })}>
                  <div className="d-grid gap-3">
                    <label className="form-label mb-0">
                      <span className="d-block mb-1">
                        {t('transactions.sourcePlatformOrderId', { defaultValue: 'Source Platform Order ID' })}
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={manualOwnTransferForm?.sourcePlatformOrderId || ''}
                        onChange={(event) => updateManualOwnTransferField('sourcePlatformOrderId', event.target.value)}
                        disabled={isSaving}
                      />
                    </label>
                    <label className="form-label mb-0">
                      <span className="d-block mb-1">
                        {t('transactions.targetPlatformOrderId', { defaultValue: 'Target Platform Order ID' })}
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={manualOwnTransferForm?.targetPlatformOrderId || ''}
                        onChange={(event) => updateManualOwnTransferField('targetPlatformOrderId', event.target.value)}
                        disabled={isSaving}
                      />
                    </label>
                    <label className="form-label mb-0">
                      <span className="d-block mb-1">
                        {t('transactions.reason', { defaultValue: 'Reason' })}
                      </span>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={manualOwnTransferForm?.reason || ''}
                        onChange={(event) => updateManualOwnTransferField('reason', event.target.value)}
                        disabled={isSaving}
                      />
                    </label>
                    <button type="button" className="btn btn-success" onClick={completeOwnTransferManually} disabled={isSaving}>
                      {t('transactions.markSucceed', { defaultValue: 'Mark Succeed' })}
                    </button>
                  </div>
                </Card>
              </div>
            ) : null}

            {(transaction.parentTransactionId || transaction.childTransactionIds?.length) ? (
              <div className="col-12 col-md-6 col-xl-3" data-testid="transaction-details-card-column">
                <Card title={t('transactions.relatedTransactions', { defaultValue: 'Related Transactions' })}>
                  <InfoList
                    items={[
                      transaction.parentTransactionId ? {
                        label: t('transactions.parentTransaction', { defaultValue: 'Parent Transaction' }),
                        value: <TransactionLink id={transaction.parentTransactionId} />,
                      } : null,
                      ...(transaction.childTransactionIds || []).map((childId, index) => ({
                        label: `${t('transactions.childTransaction', { defaultValue: 'Child Transaction' })} ${index + 1}`,
                        value: <TransactionLink id={childId} />,
                      })),
                    ].filter(Boolean)}
                  />
                </Card>
              </div>
            ) : null}

            {transaction.paymentDetails?.length ? (
              <div className="col-12 col-md-6 col-xl-3" data-testid="transaction-details-card-column">
                <Card title={t('transactions.paymentDetails', { defaultValue: 'Payment Details' })}>
                  <InfoList
                    items={transaction.paymentDetails}
                    translateLabel={(item) => (
                      item.labelKey ? t(item.labelKey, { defaultValue: item.label }) : item.label
                    )}
                  />
                </Card>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default TransactionDetailsPageView;
